// The constant chrome for every questionnaire screen: logo, time-remaining clock,
// progress bar, a centred 600px column, and the save-reassurance pinned underneath.
// Responsive: below 640px the column is full-width with side gutters and the primary
// action (rendered by each question) pins to the bottom of the viewport.
import type { ReactNode } from 'react';
import { useIsNarrow } from '../lib/useIsNarrow';

export function Shell({
  progress,
  timeLabel,
  onBack,
  showBack,
  children,
}: {
  progress: number; // 0–1
  timeLabel: string;
  onBack?: () => void;
  showBack?: boolean;
  children: ReactNode;
}) {
  const narrow = useIsNarrow();
  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div>
        <div
          style={{
            maxWidth: 1152,
            margin: '0 auto',
            padding: narrow ? '14px 20px' : '18px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/expy-man.png" alt="" width={26} height={26} style={{ display: 'block' }} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>
              Expy Health
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width={15} height={15} viewBox="0 0 16 16" fill="none" aria-hidden>
              <circle cx={8} cy={8} r={6.2} stroke="#9CA3AF" strokeWidth={1.5} />
              <path d="M8 4.8V8l2.2 1.5" stroke="#9CA3AF" strokeWidth={1.5} strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--slate)' }}>{timeLabel}</span>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ height: 4, background: 'var(--teal-tint)' }}>
          <div
            style={{
              width: `${Math.max(4, progress * 100)}%`,
              height: '100%',
              background: 'var(--teal)',
              borderRadius: '0 2px 2px 0',
              transition: 'width .3s ease',
            }}
          />
        </div>
      </div>

      {/* Body — centred column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          key={/* re-trigger fade per screen */ undefined}
          className="fade-in"
          style={{
            maxWidth: 600,
            width: '100%',
            margin: '0 auto',
            padding: narrow ? '32px 20px 120px' : '56px 24px 24px',
          }}
        >
          {showBack && onBack && (
            <button
              onClick={onBack}
              style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 18 }}
            >
              <svg width={13} height={13} viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M9 3L5 7l4 4" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-deep)' }}>Back</span>
            </button>
          )}
          {children}
        </div>
      </div>

      {/* Save reassurance */}
      {!narrow && (
        <div style={{ maxWidth: 600, width: '100%', margin: '0 auto', padding: '0 24px 40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3.5 8.4l2.8 2.8L12.5 5" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--teal-deep)' }}>
              Saved. Close this and pick up where you left off. No account needed yet.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
